import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import './ComparePages.css';

const MiricanvasCompare = () => {
  const navigate = useNavigate();

  const comparisonData = [
    { category: '서비스 유형', competitor: '디자인 템플릿 플랫폼', whatquiz: '교사 전용 AI 플랫폼', highlight: true },
    { category: 'PPT 생성', competitor: '템플릿 기반 편집', whatquiz: 'AI가 내용까지 자동 생성', highlight: true },
    { category: '학습지 생성', competitor: '템플릿 레이아웃만', whatquiz: 'AI 문제 출제 + 정답지', highlight: true },
    { category: '이미지 생성', competitor: 'AI 이미지 생성 지원', whatquiz: '교육 특화 7가지 용도', highlight: false },
    { category: '생기부 작성', competitor: '지원 안함', whatquiz: '전용 기능 (3분 완성)', highlight: true },
    { category: '수행평가 채점', competitor: '지원 안함', whatquiz: '루브릭 기반 자동 채점', highlight: true },
    { category: '교육과정 반영', competitor: '없음', whatquiz: '2026 기재요령 반영', highlight: true },
    { category: '가격', competitor: '교육용 Pro 무료(교육청 협약)', whatquiz: '무료로 시작 / PRO 월 16,667원부터', highlight: false },
    { category: '타겟', competitor: '교사 + 학생 + 일반', whatquiz: '교사 전용', highlight: false },
  ];

  const faqData = [
    { q: '미리캔버스와 왓퀴즈를 같이 쓸 수 있나요?', a: '네, 두 서비스는 보완 관계입니다. 디자인 중심 작업(포스터, 학급 게시판 등)은 미리캔버스, AI 기반 업무(생기부, PPT 내용 생성, 학습지 문제 출제, 채점)는 왓퀴즈로 사용하면 가장 효율적입니다.' },
    { q: '미리캔버스에서 왓퀴즈로 옮겨야 하나요?', a: '옮기실 필요는 없습니다. 미리캔버스는 디자인 도구이고, 왓퀴즈는 교사 업무 AI 도구로 성격이 다릅니다. 다만, PPT 내용 생성이나 학습지 문제 출제를 AI로 자동화하고 싶다면 왓퀴즈를 추가로 활용하세요.' },
    { q: '왓퀴즈도 미리캔버스처럼 템플릿이 있나요?', a: '왓퀴즈는 템플릿이 아닌 AI 생성 방식입니다. 주제를 입력하면 AI가 내용을 분석해 PPT, 학습지, 이미지를 자동 생성합니다. 매번 새로운 내용이 필요한 수업 자료에 더 적합합니다.' },
    { q: '학교 예산으로 왓퀴즈를 구매할 수 있나요?', a: '네, 에듀집(학교장터)에 등록되어 있어 학교 예산으로 구매 가능합니다. 법인카드 결제, 세금계산서 발급도 지원합니다.' },
  ];

  return (
    <div className="compare-page">
      <Helmet>
        <title>미리캔버스 vs 왓퀴즈 비교 | 교사에게 더 맞는 AI 도구는? (2026)</title>
        <meta name="description" content="미리캔버스와 왓퀴즈 완벽 비교. PPT, 학습지, 이미지 생성 기능 비교. 미리캔버스는 디자인 템플릿, 왓퀴즈는 교사 전용 AI. 생기부, 채점까지 지원하는 올인원 도구." />
        <meta name="keywords" content="미리캔버스 대안, 미리캔버스 vs 왓퀴즈, 미리캔버스 교사, 교사용 AI 비교, 미리캔버스 대체, PPT 만들기 비교, 학습지 만들기 비교, 교사 디자인 도구" />
        <link rel="canonical" href="https://whatquiz.co.kr/compare/miricanvas" />
        <meta property="og:title" content="미리캔버스 vs 왓퀴즈 | 교사에게 더 맞는 도구는?" />
        <meta property="og:description" content="미리캔버스와 왓퀴즈 완벽 비교. 디자인 vs AI. 교사에게 더 맞는 도구를 찾아보세요." />
        <meta property="og:url" content="https://whatquiz.co.kr/compare/miricanvas" />
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) })}
        </script>
      </Helmet>

      <section className="compare-hero">
        <div className="compare-hero-inner">
          <p className="compare-hero-label">2026년 최신 비교</p>
          <h1 className="compare-hero-title">미리캔버스 vs 왓퀴즈<br /><span className="compare-hero-highlight">교사에게 더 맞는 도구는?</span></h1>
          <p className="compare-hero-desc">디자인 템플릿의 미리캔버스와 교사 전용 AI의 왓퀴즈,<br />어떤 차이가 있는지 솔직하게 비교해드립니다.</p>
        </div>
      </section>

      <section className="compare-tldr">
        <div className="compare-tldr-inner">
          <h2 className="compare-section-title">한눈에 보는 결론</h2>
          <div className="compare-tldr-cards">
            <div className="compare-tldr-card">
              <h3>미리캔버스가 더 나은 경우</h3>
              <ul>
                <li>디자인 감각이 필요한 포스터, 학급 게시판 제작</li>
                <li>50만+ 템플릿 중 골라서 편집하고 싶을 때</li>
                <li>학생과 함께 디자인 수업을 진행할 때</li>
              </ul>
            </div>
            <div className="compare-tldr-card compare-tldr-card--primary">
              <h3>왓퀴즈가 더 나은 경우</h3>
              <ul>
                <li>PPT 내용까지 AI가 자동 생성해줘야 할 때</li>
                <li>학습지 문제를 AI로 자동 출제하고 싶을 때</li>
                <li>생기부 작성, 수행평가 채점이 필요할 때</li>
                <li>교육과정 반영이 중요한 자료를 만들 때</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="compare-table-section">
        <div className="compare-table-inner">
          <h2 className="compare-section-title">상세 기능 비교</h2>
          <div className="compare-table-wrapper">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>비교 항목</th>
                  <th>미리캔버스</th>
                  <th className="compare-table-highlight">왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className={row.highlight ? 'compare-table-row--highlight' : ''}>
                    <td className="compare-table-category">{row.category}</td>
                    <td>{row.competitor}</td>
                    <td className="compare-table-whatquiz">{row.whatquiz}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="compare-reasons">
        <div className="compare-reasons-inner">
          <h2 className="compare-section-title">왓퀴즈만의 차별점</h2>
          <div className="compare-reasons-grid">
            {[
              { title: 'AI가 내용까지 만든다', desc: '미리캔버스는 디자인 편집 도구여서 내용은 직접 작성해야 합니다. 왓퀴즈는 주제만 입력하면 AI가 내용 분석부터 구성까지 자동으로 처리합니다.' },
              { title: '교사 업무 올인원', desc: '미리캔버스는 디자인에 특화되어 있습니다. 왓퀴즈는 PPT, 학습지 외에도 생기부 작성, 수행평가 채점, 학생 상담 등 교사 업무 전반을 커버합니다.' },
              { title: '교육과정 반영', desc: '왓퀴즈는 2026 기재요령, 교과과정을 AI에 반영합니다. 교과서 PDF를 첨부하면 해당 내용 기반의 정확한 자료가 생성됩니다.' },
              { title: '문제 출제 + 채점', desc: '학습지 문제를 AI가 자동 출제하고, 수행평가 채점까지 연계됩니다. 미리캔버스에서는 불가능한 기능입니다.' },
            ].map((r, idx) => (
              <div key={idx} className="compare-reason-card">
                <h3>{r.title}</h3>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="compare-faq">
        <div className="compare-faq-inner">
          <h2 className="compare-section-title">자주 묻는 질문</h2>
          <div className="compare-faq-list">
            {faqData.map((faq, idx) => (
              <div key={idx} className="compare-faq-item">
                <h3 className="compare-faq-q">{faq.q}</h3>
                <p className="compare-faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="compare-cta">
        <div className="compare-cta-inner">
          <h2>교사를 위해 만들어진 AI,<br />직접 경험해보세요</h2>
          <p>가입만 하면 무료로 시작할 수 있습니다</p>
          <div className="compare-cta-buttons">
            <button className="compare-cta-primary" onClick={() => navigate('/ai-ppt')}>무료로 AI PPT 만들기</button>
            <button className="compare-cta-secondary" onClick={() => navigate('/pricing')}>요금제 보기</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MiricanvasCompare;
